import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

/**
 * Montreal is in the America/Montreal timezone (EST/EDT)
 * UTC-5 in winter (EST), UTC-4 in summer (EDT)
 * 
 * DST rules for Montreal:
 * - Starts: Second Sunday in March at 2:00 AM
 * - Ends: First Sunday in November at 2:00 AM
 */

/**
 * Check if a date is in Daylight Saving Time for Montreal
 */
export function isMontrealDST(date: Date): boolean {
    const year = date.getUTCFullYear();
    
    // DST starts second Sunday in March at 2:00 AM
    const marchFirst = new Date(Date.UTC(year, 2, 1, 7, 0, 0)); // March 1 at 2 AM EST (7 UTC)
    const marchFirstDay = marchFirst.getUTCDay();
    const dstStart = new Date(Date.UTC(year, 2, 8 + (7 - marchFirstDay) % 7, 7, 0, 0));
    
    // DST ends first Sunday in November at 2:00 AM
    const novFirst = new Date(Date.UTC(year, 10, 1, 6, 0, 0)); // Nov 1 at 2 AM EDT (6 UTC)
    const novFirstDay = novFirst.getUTCDay();
    const dstEnd = new Date(Date.UTC(year, 10, 1 + (7 - novFirstDay) % 7, 6, 0, 0));
    
    return date >= dstStart && date < dstEnd;
}

/**
 * Convert UTC date to Montreal local time
 */
function toMontrealTime(utcDate: Date): Date {
    const isDST = isMontrealDST(utcDate);
    const offset = isDST ? -4 : -5; // EDT is UTC-4, EST is UTC-5
    
    const montrealTime = new Date(utcDate);
    montrealTime.setHours(montrealTime.getUTCHours() + offset);
    
    return montrealTime;
}

/**
 * Format a UTC date string to Montreal timezone with locale-specific formatting
 * 
 * @param utcDateString - ISO date string in UTC
 * @param locale - 'en' or 'fr'
 * @returns Formatted date string in Montreal timezone
 * 
 * @example
 * formatMontrealTime('2024-03-15T14:00:00Z', 'en') 
 * // => "March 15, 2024 at 10:00 AM" (EST: UTC-5)
 * 
 * formatMontrealTime('2024-07-15T14:00:00Z', 'fr')
 * // => "15 juillet 2024 à 10:00" (EDT: UTC-4)
 */
export function formatMontrealTime(utcDateString: string, locale: 'en' | 'fr'): string {
    const utcDate = new Date(utcDateString);
    const montrealDate = toMontrealTime(utcDate);
    
    if (locale === 'fr') {
        // French format: "15 mars 2024 à 14:00"
        return format(montrealDate, "d MMMM yyyy 'à' HH:mm", { locale: undefined });
    } else {
        // English format: "March 15, 2024 at 2:00 PM"
        return format(montrealDate, "MMMM d, yyyy 'at' h:mm a", { locale: undefined });
    }
}

/**
 * Format a UTC date string to Montreal timezone with short format
 * 
 * @param utcDateString - ISO date string in UTC
 * @param locale - 'en' or 'fr'
 * @returns Short formatted date string
 * 
 * @example
 * formatMontrealTimeShort('2024-03-15T14:00:00Z', 'en') 
 * // => "Mar 15, 10:00 AM"
 */
export function formatMontrealTimeShort(utcDateString: string, locale: 'en' | 'fr'): string {
    const utcDate = new Date(utcDateString);
    const montrealDate = toMontrealTime(utcDate);
    
    if (locale === 'fr') {
        return format(montrealDate, "d MMM, HH:mm", { locale: undefined });
    } else {
        return format(montrealDate, "MMM d, h:mm a", { locale: undefined });
    }
}

/**
 * Format a UTC date string to voting deadline format (e.g., "DEC 01 / 23:59" or "01 DÉC / 23:59")
 * 
 * @param utcDateString - ISO date string in UTC
 * @param locale - 'en' or 'fr'
 * @returns Formatted date string in voting deadline format
 * 
 * @example
 * formatVotingDeadline('2024-12-01T23:59:00Z', 'en') 
 * // => "DEC 01 / 23:59"
 * 
 * formatVotingDeadline('2024-12-01T23:59:00Z', 'fr')
 * // => "01 DÉC / 23:59"
 */
export function formatVotingDeadline(utcDateString: string, locale: 'en' | 'fr'): string {
    const utcDate = new Date(utcDateString);
    const montrealDate = toMontrealTime(utcDate);
    
    if (locale === 'fr') {
        // French format: "01 DÉC / 23:59"
        const day = format(montrealDate, 'dd', { locale: fr });
        const month = format(montrealDate, 'MMM', { locale: fr }).toUpperCase();
        const time = format(montrealDate, 'HH:mm', { locale: fr });
        return `${day} ${month} / ${time}`;
    } else {
        // English format: "DEC 01 / 23:59"
        const month = format(montrealDate, 'MMM', { locale: undefined }).toUpperCase();
        const day = format(montrealDate, 'dd', { locale: undefined });
        const time = format(montrealDate, 'HH:mm', { locale: undefined });
        return `${month} ${day} / ${time}`;
    }
}

/**
 * Parse a date string as Montreal timezone for consistent comparisons
 * If the date string already has timezone info, it's parsed normally.
 * If it doesn't have timezone info, it's interpreted as Montreal time.
 * 
 * @param dateString - ISO date string (with or without timezone)
 * @returns Date object representing the date in Montreal timezone
 */
export function parseAsMontrealTime(dateString: string | null | undefined): Date {
    // Handle null/undefined
    if (!dateString) {
        return new Date(NaN); // Return invalid date
    }
    
    try {
        // If the date string already has timezone info (Z or +/-offset), parse it directly
        if (dateString.includes('Z') || /[+-]\d{2}:?\d{2}$/.test(dateString)) {
            return new Date(dateString);
        }
        
        // Otherwise, the date string is without timezone info
        // We need to interpret it as Montreal time
        // First, parse it as if it's a naive datetime
        const naiveDate = new Date(dateString);
        
        // Check if parsing failed (invalid date)
        if (isNaN(naiveDate.getTime())) {
            return naiveDate;
        }
        
        // Get the Montreal timezone offset for this date (handles DST)
        // getTimezoneOffset() returns minutes from local time to UTC (positive = behind UTC, negative = ahead)
        // Montreal EST is UTC-5, so offset is +300 minutes
        // Montreal EDT is UTC-4, so offset is +240 minutes
        const isDST = isMontrealDST(naiveDate);
        const montrealOffsetMinutes = isDST ? 4 * 60 : 5 * 60; // EDT is +240, EST is +300
        
        // The naive date was parsed in the user's local timezone
        // We need to convert it to what it would be if it were Montreal time
        // Get the UTC timestamp of the naive date
        const naiveUTC = naiveDate.getTime();
        
        // Get the user's timezone offset (positive = behind UTC, negative = ahead)
        const userOffsetMinutes = naiveDate.getTimezoneOffset();
        
        // Calculate the difference: Montreal offset - user offset
        // This tells us how much to adjust the UTC timestamp
        // If user is ahead of Montreal, adjustment is negative (subtract time)
        // If user is behind Montreal, adjustment is positive (add time)
        const adjustmentMinutes = montrealOffsetMinutes - userOffsetMinutes;
        
        // Create a new date adjusted for Montreal timezone
        return new Date(naiveUTC + adjustmentMinutes * 60 * 1000);
    } catch (error) {
        // If anything fails, return invalid date
        console.error('Error parsing date as Montreal time:', dateString, error);
        return new Date(NaN);
    }
}

/**
 * Get current date/time for consistent date comparisons
 * All festival-related date comparisons should use this instead of new Date()
 * 
 * Returns the current UTC time. Since JavaScript Date objects store time as UTC timestamps
 * internally, this works correctly for comparisons with dates parsed as Montreal time.
 * 
 * The key is that parseAsMontrealTime() converts Montreal time to UTC, and this function
 * returns current UTC time, so comparisons work correctly regardless of user's timezone.
 * 
 * @returns Date object representing current UTC time
 */
export function getMontrealNow(): Date {
    // Return current UTC time - JavaScript Date always stores UTC internally
    // Comparisons with parseAsMontrealTime() will work because both are UTC timestamps
    return new Date();
}

/**
 * Check if voting is currently open based on Montreal time
 */
export function isVotingOpenNow(startUTC: string, endUTC: string): boolean {
    const now = new Date();
    const start = new Date(startUTC);
    const end = new Date(endUTC);
    
    return now >= start && now <= end;
}

